"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { Button } from "@/components/ui/button";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import { showToast } from "@/lib/toast";
import { V2_ENDPOINT } from "@/consts";

interface SlackOAuthURLResponse {
  code: number;
  data: {
    url: string;
  };
}

interface SlackOAuthConnectButtonProps {
  disabled?: boolean;
  onSuccess?: () => void;
  onPopupClosed?: () => void;
  connectorName?: string;
}

const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 700;

export function SlackOAuthConnectButton({
  disabled,
  onSuccess,
  onPopupClosed,
  connectorName,
}: SlackOAuthConnectButtonProps) {
  const [isConnecting, setIsConnecting] = useState(false);
  const popupRef = useRef<Window | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onPopupClosedRef = useRef(onPopupClosed);
  onPopupClosedRef.current = onPopupClosed;
  const receivedMessageRef = useRef(false);

  const cleanup = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    popupRef.current = null;
    setIsConnecting(false);
  }, []);

  // Listen for postMessage from the OAuth callback popup.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type !== "slack-oauth-result") return;

      if (process.env.NODE_ENV === "production" && V2_ENDPOINT) {
        try {
          const expectedOrigin = new URL(V2_ENDPOINT).origin;
          if (event.origin !== expectedOrigin) return;
        } catch {
          console.warn("[SlackOAuth] V2_ENDPOINT is not a valid URL, skipping origin check:", V2_ENDPOINT);
        }
      }

      receivedMessageRef.current = true;
      cleanup();

      if (event.data.success) {
        showToast.success(`Slack workspace connected: ${event.data.connectorName || "Success"}`);
        void onSuccessRef.current?.();
      } else {
        showToast.error(event.data.error || "Failed to connect Slack workspace");
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [cleanup]);

  const handleConnect = async () => {
    setIsConnecting(true);
    receivedMessageRef.current = false;
    try {
      const nameParam = connectorName ? `?name=${encodeURIComponent(connectorName)}` : "";
      const response = await authenticatedFetch<SlackOAuthURLResponse>(
        `${V2_ENDPOINT}/v1/integrations/slack/oauth-url${nameParam}`,
      );

      if (!response.ok) {
        let errorMessage = "Failed to get Slack OAuth URL";
        try {
          const errorData = await response.json();
          if (errorData?.message) errorMessage = errorData.message;
        } catch {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      if (data.code !== 0 || !data.data?.url) {
        throw new Error("Failed to get Slack authorization URL");
      }

      // Open Slack OAuth in a centered popup window.
      const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2;
      const top = window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2;
      const popup = window.open(
        data.data.url,
        "slack-oauth",
        `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},toolbar=no,menubar=no`,
      );

      if (!popup) {
        // Popup blocked — fall back to redirect.
        window.location.href = data.data.url;
        return;
      }

      popupRef.current = popup;

      // Poll for popup close — acts as fallback when postMessage is lost
      // (cross-origin restrictions, browser policies, race conditions).
      pollRef.current = setInterval(() => {
        if (popup.closed) {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          popupRef.current = null;
          setIsConnecting(false);

          if (!receivedMessageRef.current) {
            void onPopupClosedRef.current?.();
          }
        }
      }, 500);
    } catch (error) {
      showToast.error(error instanceof Error ? error.message : "Failed to start Slack OAuth flow");
      setIsConnecting(false);
    }
  };

  return (
    <div className="flex justify-center">
      <Button
        type="button"
        size="lg"
        onClick={handleConnect}
        disabled={disabled || isConnecting}
        className="px-6 py-3.5"
      >
        {isConnecting ? (
          <span className="flex items-center gap-2 text-sm">
            <PixelLoader size="sm" />
            Connecting to Slack...
          </span>
        ) : (
          <span className="flex items-center gap-2 text-sm">Connect with Slack</span>
        )}
      </Button>
    </div>
  );
}
